import * as React from "react"

const TOAST_LIMIT = 3
const TOAST_REMOVE_DELAY = 5000

type ToasterToast = {
  id: string
  title?: string
  description?: string
  variant?: "default" | "destructive"
}

let count = 0
function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

type Action =
  | { type: "ADD_TOAST"; toast: ToasterToast }
  | { type: "UPDATE_TOAST"; toast: Partial<ToasterToast> }
  | { type: "REMOVE_TOAST"; toastId?: string }

interface State {
  toasts: ToasterToast[]
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

// Auto-dismiss: a toast removes itself after TOAST_REMOVE_DELAY unless dismissed first.
const scheduleAutoDismiss = (toastId: string) => {
  if (toastTimeouts.has(toastId)) return
  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({ type: "REMOVE_TOAST", toastId })
  }, TOAST_REMOVE_DELAY)
  toastTimeouts.set(toastId, timeout)
}

const clearAutoDismiss = (toastId: string) => {
  const timeout = toastTimeouts.get(toastId)
  if (timeout) clearTimeout(timeout)
  toastTimeouts.delete(toastId)
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST":
      return { ...state, toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT) }
    case "UPDATE_TOAST":
      return { ...state, toasts: state.toasts.map((t) => t.id === action.toast.id ? { ...t, ...action.toast } : t) }
    case "REMOVE_TOAST":
      if (action.toastId === undefined) return { ...state, toasts: [] }
      return { ...state, toasts: state.toasts.filter((t) => t.id !== action.toastId) }
    default:
      return state
  }
}

const listeners: Array<(state: State) => void> = []
let memoryState: State = { toasts: [] }

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => listener(memoryState))
}

// `persist` opts out of the auto-dismiss timer. It's for notices describing a
// condition that outlives the toast — a stale-data warning stays true until the
// next successful fetch, so vanishing after 5s puts the user back in the dark.
// Opt-in rather than keyed off variant: most destructive toasts report a
// user-triggered action that failed, where the user is watching and the UI has
// other signals, so auto-dismiss is right for them.
type Toast = Omit<ToasterToast, "id"> & { persist?: boolean }

function dismiss(toastId?: string) {
  if (toastId) clearAutoDismiss(toastId)
  else toastTimeouts.forEach((_, id) => clearAutoDismiss(id))
  dispatch({ type: "REMOVE_TOAST", toastId })
}

function toast({ persist, ...props }: Toast) {
  const id = genId()
  dispatch({ type: "ADD_TOAST", toast: { ...props, id } })
  if (!persist) scheduleAutoDismiss(id)
  // Cancel timers for any toast evicted by the TOAST_LIMIT cap, so a burst
  // doesn't leave orphaned timeouts firing no-op removals.
  const live = new Set(memoryState.toasts.map((t) => t.id))
  toastTimeouts.forEach((_, timerId) => {
    if (!live.has(timerId)) clearAutoDismiss(timerId)
  })
  return {
    id,
    dismiss: () => dismiss(id),
    update: (props: ToasterToast) => dispatch({ type: "UPDATE_TOAST", toast: { ...props, id } }),
    // Whether this toast is still on screen. A caller holding a handle to a
    // persisted toast can't otherwise tell: the user can dismiss it with the X,
    // and TOAST_LIMIT can evict it. Both leave the handle looking live, so a
    // caller gating "already shown?" on the handle alone would go quiet for
    // good on a condition that's still true.
    isActive: () => memoryState.toasts.some((t) => t.id === id),
  }
}

function useToast() {
  const [state, setState] = React.useState<State>(memoryState)
  React.useEffect(() => {
    listeners.push(setState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) listeners.splice(index, 1)
    }
  }, [])
  return { ...state, toast, dismiss }
}

export { useToast, toast }
